import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiDownload,
    FiFileText,
    FiRefreshCw,
    FiUploadCloud,
    FiXCircle,
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { importEcmwfV2ScheduledForecastExcel } from '../../services/api';
import {
    ECMWF_IMPORT_ROW_STATUS,
    ECMWF_IMPORT_TEMPLATE_URL,
    canConfirmEcmwfImport,
    getAdminRoleFromToken,
    getDryRunProcessableCount,
    getEcmwfImportErrorMessage,
    getImportDetailRows,
    isAdministratorRole,
    validateEcmwfImportFile,
} from '../../utils/ecmwfExcelImport';
import './EcmwfV2ExcelImportTab.css';

const STATUS_CONFIG = {
    [ECMWF_IMPORT_ROW_STATUS.VALID]: { label: 'Valide', bg: 'success' },
    [ECMWF_IMPORT_ROW_STATUS.MATCHED]: { label: 'Matchée', bg: 'success' },
    [ECMWF_IMPORT_ROW_STATUS.CREATED]: { label: 'Créée', bg: 'success' },
    [ECMWF_IMPORT_ROW_STATUS.UPDATED]: { label: 'Mise à jour', bg: 'primary' },
    [ECMWF_IMPORT_ROW_STATUS.NO_MATCH]: { label: 'Aucune police', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.AMBIGUOUS_LOCATION]: { label: 'Localisation ambiguë', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.DUPLICATE_TARGET]: { label: 'Doublon', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.AMBIGUOUS_FORECAST]: { label: 'Forecast ambigu', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.UPSERT_ERROR]: { label: 'Erreur', bg: 'danger' },
};

const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes)) return '—';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
};

const statusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status || '—', bg: 'secondary' };
    return <Badge bg={config.bg} text={config.text}>{config.label}</Badge>;
};

const SummaryCard = ({ label, value, variant = 'primary' }) => (
    <Col xs={12} sm={6} xl={3}>
        <Card className={`ecmwf-import-stat ecmwf-import-stat--${variant}`}>
            <Card.Body>
                <div className="text-muted small">{label}</div>
                <div className="ecmwf-import-stat__value">{value ?? 0}</div>
            </Card.Body>
        </Card>
    </Col>
);

const RowMessages = ({ errors, warnings }) => {
    const errorList = Array.isArray(errors) ? errors : [];
    const warningList = Array.isArray(warnings) ? warnings : [];
    if (!errorList.length && !warningList.length) return '—';
    return (
        <div className="ecmwf-import-messages">
            {errorList.map((message, index) => (
                <div className="text-danger" key={`error-${index}`}>{message}</div>
            ))}
            {warningList.map((message, index) => (
                <div className="text-warning-emphasis" key={`warning-${index}`}>{message}</div>
            ))}
        </div>
    );
};

const ResultView = ({ result, title }) => {
    const summary = result?.summary || {};
    const rows = useMemo(() => getImportDetailRows(result), [result]);
    if (!result) return null;

    const isFinal = result.dry_run === false;
    return (
        <div className="mt-4">
            <Alert variant={result.status_code === 200 ? 'success' : 'warning'}>
                <div className="d-flex align-items-start gap-2">
                    {result.status_code === 200
                        ? <FiCheckCircle className="mt-1 flex-shrink-0" aria-hidden />
                        : <FiAlertTriangle className="mt-1 flex-shrink-0" aria-hidden />}
                    <div>
                        <strong>{title}</strong>
                        {result.message && <div className="mt-1">{result.message}</div>}
                        {result.timestamp && (
                            <small className="text-muted d-block mt-1">
                                {new Date(result.timestamp).toLocaleString('fr-FR')}
                            </small>
                        )}
                    </div>
                </div>
            </Alert>

            <Row className="g-3 mb-4">
                <SummaryCard label="Total des lignes" value={summary.total_rows} />
                <SummaryCard
                    label={isFinal ? 'Forecasts traités' : 'Lignes Excel matchées'}
                    value={isFinal
                        ? summary.rows_processed
                        : (summary.excel_rows_matched ?? summary.rows_matched ?? summary.rows_valid)}
                    variant="success"
                />
                {!isFinal && (
                    <SummaryCard
                        label="Polices matchées"
                        value={summary.policies_matched ?? summary.rows_matched}
                        variant="primary"
                    />
                )}
                {isFinal ? (
                    <>
                        <SummaryCard label="Créées" value={summary.rows_created} variant="success" />
                        <SummaryCard label="Mises à jour" value={summary.rows_updated} variant="primary" />
                        <SummaryCard label="Erreurs d’écriture" value={summary.rows_upsert_errors} variant="danger" />
                    </>
                ) : (
                    <>
                        <SummaryCard label="Sans police correspondante" value={summary.rows_no_matching_policy} variant="warning" />
                        <SummaryCard label="Localisations ambiguës" value={summary.rows_ambiguous_location} variant="warning" />
                    </>
                )}
                <SummaryCard label="Lignes avec erreurs" value={summary.rows_with_errors} variant="danger" />
                <SummaryCard label="Warnings" value={summary.warnings_count} variant="warning" />
            </Row>

            {isFinal && (
                <Alert variant={summary.email_queued ? 'info' : 'secondary'}>
                    <strong>Email administrateur :</strong>{' '}
                    {summary.email_queued ? 'programmé' : 'non programmé'}
                    {summary.email_recipients_count != null
                        ? ` (${summary.email_recipients_count} destinataire(s))`
                        : ''}
                </Alert>
            )}

            {rows.length > 0 && (
                <Card>
                    <Card.Header className="fw-semibold">Détail par ligne Excel</Card.Header>
                    <Card.Body className="p-0">
                        <div className="ecmwf-import-table-wrap">
                            <Table striped hover responsive className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Ligne</th>
                                        <th>Statut</th>
                                        <th>Code police</th>
                                        <th>Référence</th>
                                        <th>Date du séjour</th>
                                        <th>Coordonnées</th>
                                        <th>Action</th>
                                        <th>Messages</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={`${row.excel_row}-${row.status}-${index}`}>
                                            <td><code>{row.excel_row ?? '—'}</code></td>
                                            <td>{statusBadge(row.status)}</td>
                                            <td>{row.match?.policy_code || '—'}</td>
                                            <td>
                                                {row.match?.reference_name
                                                    || row.row?.reference_name
                                                    || row.matching_keys?.reference_name
                                                    || '—'}
                                            </td>
                                            <td>
                                                {row.row?.trip_day
                                                    || row.matching_keys?.trip_day
                                                    || '—'}
                                            </td>
                                            <td>
                                                {(row.row?.latitude ?? row.matching_keys?.latitude) != null
                                                    ? `${row.row?.latitude ?? row.matching_keys?.latitude}, ${row.row?.longitude ?? row.matching_keys?.longitude}`
                                                    : '—'}
                                            </td>
                                            <td>{row.action ? statusBadge(row.action) : '—'}</td>
                                            <td><RowMessages errors={row.errors} warnings={row.warnings} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

const EcmwfV2ExcelImportTab = () => {
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const role = getAdminRoleFromToken(accessToken || localStorage.getItem('admin_access_token'));
    const knownRestrictedRole = role && !isAdministratorRole(role);

    const [file, setFile] = useState(null);
    const [validatedFile, setValidatedFile] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [requestError, setRequestError] = useState(null);
    const [dryRunResult, setDryRunResult] = useState(null);
    const [finalResult, setFinalResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const abortRef = useRef(null);
    const requestVersionRef = useRef(0);
    const inputRef = useRef(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    const invalidatePendingRequest = () => {
        requestVersionRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
        setLoading(false);
    };

    const selectFile = (nextFile) => {
        invalidatePendingRequest();
        setRequestError(null);
        setDryRunResult(null);
        setFinalResult(null);
        setValidatedFile(null);
        setShowConfirm(false);
        const validation = validateEcmwfImportFile(nextFile);
        if (!validation.valid) {
            setFile(null);
            setFileError(validation.message);
            return;
        }
        setFile(nextFile);
        setFileError(null);
    };

    const runImport = async (dryRun) => {
        const validation = validateEcmwfImportFile(file);
        if (!validation.valid) {
            setFileError(validation.message);
            return;
        }

        const version = requestVersionRef.current + 1;
        requestVersionRef.current = version;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setRequestError(null);
        if (dryRun) {
            setDryRunResult(null);
            setFinalResult(null);
        }

        try {
            const response = await importEcmwfV2ScheduledForecastExcel(file, dryRun, controller.signal);
            if (requestVersionRef.current !== version || controller.signal.aborted) return;
            if (dryRun) {
                setValidatedFile(file);
                setDryRunResult(response);
            } else {
                setFinalResult(response);
                setShowConfirm(false);
            }
        } catch (error) {
            if (controller.signal.aborted || requestVersionRef.current !== version) return;
            setRequestError(getEcmwfImportErrorMessage(error));
            if (!dryRun) setShowConfirm(false);
        } finally {
            if (requestVersionRef.current === version) {
                setLoading(false);
                abortRef.current = null;
            }
        }
    };

    const reset = () => {
        invalidatePendingRequest();
        setFile(null);
        setValidatedFile(null);
        setFileError(null);
        setRequestError(null);
        setDryRunResult(null);
        setFinalResult(null);
        setShowConfirm(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    const canConfirm = canConfirmEcmwfImport({
        dryRunResult,
        validatedFile,
        selectedFile: file,
        loading,
    });
    const processableRows = getDryRunProcessableCount(dryRunResult);
    const totalRows = Number(dryRunResult?.summary?.total_rows || 0);
    const matchedExcelRows = Number(
        dryRunResult?.summary?.excel_rows_matched
        ?? dryRunResult?.summary?.rows_matched
        ?? dryRunResult?.summary?.rows_valid
        ?? 0
    );
    const ignoredRows = Math.max(0, totalRows - matchedExcelRows);

    if (knownRestrictedRole) {
        return (
            <Alert variant="warning" className="mb-0">
                <FiAlertTriangle className="me-2" />
                Cette fonctionnalité est réservée aux administrateurs.
            </Alert>
        );
    }

    return (
        <div className="ecmwf-import">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h5 className="mb-1">Import Excel ECMWF V2</h5>
                    <p className="text-muted mb-0">
                        Validez le fichier avant de confirmer la création ou la mise à jour des forecasts.
                    </p>
                </div>
                <Button
                    as="a"
                    href={ECMWF_IMPORT_TEMPLATE_URL}
                    download
                    variant="outline-primary"
                    className="d-flex align-items-center"
                >
                    <FiDownload className="me-2" />
                    Télécharger le modèle Excel
                </Button>
            </div>

            <Alert variant="light" className="border">
                <strong>Colonnes attendues :</strong>{' '}
                Latitude · Longitude · Données pluie · Données température · Date du séjour · Nom référence
                <div className="small text-muted mt-1">
                    Les cellules météo doivent conserver leur liste JSON d’objets. Le fichier n’est ni parsé ni modifié dans le navigateur.
                </div>
            </Alert>

            <div
                className={`ecmwf-import-dropzone${dragActive ? ' is-dragging' : ''}${fileError ? ' is-invalid' : ''}`}
                onDragEnter={(event) => {
                    event.preventDefault();
                    setDragActive(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => {
                    event.preventDefault();
                    if (event.currentTarget === event.target) setDragActive(false);
                }}
                onDrop={(event) => {
                    event.preventDefault();
                    setDragActive(false);
                    selectFile(event.dataTransfer.files?.[0] || null);
                }}
            >
                <FiUploadCloud size={36} aria-hidden />
                <div className="fw-semibold mt-2">Déposez votre fichier .xlsx ici</div>
                <div className="text-muted small mb-3">ou sélectionnez-le depuis votre ordinateur</div>
                <Button
                    type="button"
                    variant="outline-primary"
                    onClick={() => inputRef.current?.click()}
                    disabled={loading}
                >
                    Sélectionner un fichier
                </Button>
                <Form.Control
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="visually-hidden"
                    onChange={(event) => selectFile(event.target.files?.[0] || null)}
                    disabled={loading}
                />
            </div>

            {fileError && <div className="text-danger small mt-2">{fileError}</div>}

            {file && (
                <Card className="mt-3">
                    <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
                        <div className="d-flex align-items-center gap-2">
                            <FiFileText size={22} className="text-primary" aria-hidden />
                            <div>
                                <div className="fw-semibold">{file.name}</div>
                                <div className="text-muted small">{formatFileSize(file.size)}</div>
                            </div>
                        </div>
                        <Button
                            variant="primary"
                            onClick={() => runImport(true)}
                            disabled={loading}
                        >
                            {loading && !finalResult ? (
                                <Spinner size="sm" animation="border" className="me-2" />
                            ) : (
                                <FiCheckCircle className="me-2" />
                            )}
                            Valider le fichier
                        </Button>
                    </Card.Body>
                </Card>
            )}

            {requestError && (
                <Alert variant="danger" className="mt-3" dismissible onClose={() => setRequestError(null)}>
                    <FiXCircle className="me-2" />
                    {requestError}
                    <div className="mt-2">
                        <Button variant="outline-danger" size="sm" onClick={() => runImport(!dryRunResult)}>
                            Réessayer
                        </Button>
                    </div>
                </Alert>
            )}

            <ResultView result={dryRunResult} title="Résultat de la validation (dry-run)" />

            {dryRunResult && !finalResult && (
                <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                    <Button variant="outline-secondary" onClick={reset} disabled={loading}>
                        Importer un autre fichier
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => setShowConfirm(true)}
                        disabled={!canConfirm}
                    >
                        Confirmer l&apos;import
                    </Button>
                </div>
            )}

            <ResultView result={finalResult} title="Résultat de l’import" />

            {finalResult && (
                <div className="d-flex justify-content-end mt-3">
                    <Button variant="outline-primary" onClick={reset}>
                        <FiRefreshCw className="me-2" />
                        Importer un autre fichier
                    </Button>
                </div>
            )}

            <Modal show={showConfirm} onHide={() => !loading && setShowConfirm(false)} centered>
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>Confirmer l’import ECMWF V2</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Vous allez relancer le même fichier avec <code>dry_run=false</code>.</p>
                    <ul className="mb-3">
                        <li><strong>{processableRows}</strong> forecast(s) seront traités.</li>
                        <li><strong>{matchedExcelRows}</strong> ligne(s) Excel sont matchées.</li>
                        <li><strong>{ignoredRows}</strong> ligne(s) Excel seront ignorées.</li>
                    </ul>
                    <Alert variant="warning" className="mb-0">
                        Une création ou une mise à jour peut être effectuée par clé
                        {' '}<code>(policy_id, trip_day)</code>. Un résumé sera envoyé aux administrateurs après succès.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={loading}>
                        Annuler
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => runImport(false)}
                        disabled={loading || !canConfirm}
                    >
                        {loading && <Spinner size="sm" animation="border" className="me-2" />}
                        Confirmer et importer
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EcmwfV2ExcelImportTab;

